import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { employeeMessageService } from '../../services/employeeMessageService';
import { useAuth } from '../../hooks/useAuth';
import { formatDateDisplay } from '../../utils/dateUtils';

const authorLabel = (row, myId) => {
  if (Number(row.author_id) === Number(myId)) return 'You';
  return [row.author_first_name, row.author_last_name].filter(Boolean).join(' ').trim() || 'Someone';
};

const groupThreads = (rows) => {
  const roots = [];
  const byRoot = new Map();
  for (const row of rows) {
    if (row.parent_id == null) {
      const thread = { root: row, replies: [] };
      roots.push(thread);
      byRoot.set(row.id, thread);
    }
  }
  for (const row of rows) {
    if (row.parent_id != null) {
      const thread = byRoot.get(row.parent_id);
      if (thread) thread.replies.push(row);
    }
  }
  return roots.sort((a, b) => {
    const aLast = a.replies[a.replies.length - 1]?.submitted_at || a.root.submitted_at;
    const bLast = b.replies[b.replies.length - 1]?.submitted_at || b.root.submitted_at;
    return new Date(bLast) - new Date(aLast);
  });
};

const EmployeeMessages = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [replyForId, setReplyForId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || 'You';
  const threads = useMemo(() => groupThreads(items), [items]);

  const load = async () => {
    const res = await employeeMessageService.listMine();
    setItems(res.data || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        await load();
        window.dispatchEvent(new CustomEvent('employee-messages-updated'));
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load messages');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const resetForm = () => {
    setMessage('');
    setFormOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await employeeMessageService.createMine({ message: message.trim() });
      window.dispatchEvent(new CustomEvent('employee-messages-updated'));
      setSuccess('Message posted.');
      resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (rootId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    setError('');
    setSuccess('');
    try {
      await employeeMessageService.replyMine(rootId, { message: replyText.trim() });
      window.dispatchEvent(new CustomEvent('employee-messages-updated'));
      setReplyForId(null);
      setReplyText('');
      setSuccess('Reply posted.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reply');
    } finally {
      setReplying(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      await employeeMessageService.deleteMine(deleteRow.id);
      window.dispatchEvent(new CustomEvent('employee-messages-updated'));
      setDeleteRow(null);
      setSuccess('Message deleted.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete message');
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = (row) =>
    row.status === 'unread' &&
    Number(row.author_id) === Number(user?.id) &&
    (row.parent_id != null || !items.some((m) => Number(m.parent_id) === Number(row.id)));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Message Board
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)} disabled={formOpen}>
          New message
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Leave a note — clock in/out issues, supplies needed (grinding discs, welding nozzles), or other reminders. Your
        name is attached automatically. You can reply when someone gets back to you.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {formOpen && (
        <Paper sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            size="small"
            label="Your name"
            value={displayName}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            required
            fullWidth
            size="small"
            label="Message"
            multiline
            minRows={3}
            inputProps={{ maxLength: 1000 }}
            helperText={`${message.length}/1000`}
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
            placeholder="e.g. Forgot to clock out yesterday · Need more grinding discs"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={resetForm} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting || !message.trim()}>
              {submitting ? 'Sending…' : 'Post message'}
            </Button>
          </Box>
        </Paper>
      )}

      {threads.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No messages yet.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {threads.map(({ root, replies }) => {
            const archived = root.status === 'archived';
            return (
              <Paper key={root.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    {authorLabel(root, user?.id)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {root.submitted_at ? formatDateDisplay(root.submitted_at) : ''}
                    </Typography>
                  </Typography>
                  {root.status === 'unread' && Number(root.author_id) === Number(user?.id) && (
                    <Chip size="small" label="Awaiting reply" color="warning" />
                  )}
                  {archived && <Chip size="small" label="Archived" />}
                </Box>
                <Typography sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{root.message}</Typography>

                {replies.map((reply) => (
                  <Box
                    key={reply.id}
                    sx={{
                      ml: 2,
                      pl: 1.5,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                      py: 1
                    }}
                  >
                    <Typography variant="subtitle2">
                      {authorLabel(reply, user?.id)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {reply.submitted_at ? formatDateDisplay(reply.submitted_at) : ''}
                      </Typography>
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{reply.message}</Typography>
                    {canDelete(reply) && (
                      <Button size="small" color="error" sx={{ mt: 0.5 }} onClick={() => setDeleteRow(reply)}>
                        Delete
                      </Button>
                    )}
                  </Box>
                ))}

                {!archived && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    {replyForId === root.id ? (
                      <Box>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          placeholder="Write a reply…"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value.slice(0, 1000))}
                          inputProps={{ maxLength: 1000 }}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => {
                              setReplyForId(null);
                              setReplyText('');
                            }}
                            disabled={replying}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={replying || !replyText.trim()}
                            onClick={() => handleReply(root.id)}
                          >
                            {replying ? 'Sending…' : 'Reply'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={() => setReplyForId(root.id)}>
                          Reply
                        </Button>
                        {canDelete(root) && (
                          <Button size="small" color="error" variant="outlined" onClick={() => setDeleteRow(root)}>
                            Delete
                          </Button>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={!!deleteRow} onClose={() => !deleting && setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete message?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this message while it's still awaiting review? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeMessages;
